import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { FloppyDisk, ArrowCounterClockwise, Eye, FilePdf } from '@phosphor-icons/react';

const TYPES = [
  { id: 'factuur', label: 'Factuur', icon: '📄' },
  { id: 'creditnota', label: 'Creditnota', icon: '↩️' },
  { id: 'offerte', label: 'Offerte', icon: '✍️' },
  { id: 'bestelbon', label: 'Bestelbon', icon: '🛒' },
  { id: 'leverbon', label: 'Leveringsbon', icon: '🚚' },
  { id: 'proforma', label: 'Proforma', icon: '📋' },
];

const COLOR_PRESETS = [
  { label: 'Zwart/Charcoal', value: '#2c2c2c' },
  { label: 'Blauw', value: '#002FA7' },
  { label: 'Groen', value: '#059669' },
  { label: 'Oranje', value: '#F59E0B' },
  { label: 'Paars', value: '#7C3AED' },
  { label: 'Rood', value: '#D32F2F' },
  { label: 'Roze', value: '#E91E63' },
  { label: 'Donkergroen', value: '#065F46' },
];

export default function PdfLayoutEditor() {
  const [layouts, setLayouts] = useState(null);
  const [activeType, setActiveType] = useState('factuur');
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    API.get('/pdf-layouts').then(r => setLayouts(r.data)).catch(() => {});
  }, []);

  const cur = layouts?.[activeType] || {};

  const updateField = (field, val) => {
    setLayouts(p => ({ ...p, [activeType]: { ...p[activeType], [field]: val } }));
    setDirty(true);
  };

  const save = async () => {
    try {
      await API.put('/pdf-layouts', { layouts });
      setDirty(false);
      setMsg('✅ Opgeslagen — nieuwe layout wordt gebruikt voor alle volgende PDFs');
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Opslaan mislukt'));
    }
  };

  const resetType = () => {
    if (!window.confirm(`Reset layout voor "${TYPES.find(t => t.id === activeType)?.label}" naar standaard?`)) return;
    // We resetten door een lege object te sturen — backend mergt met defaults
    setLayouts(p => {
      const reload = async () => {
        await API.put('/pdf-layouts', { layouts: { ...p, [activeType]: null } });
        const r = await API.get('/pdf-layouts');
        setLayouts(r.data);
        setDirty(false);
        setMsg('🔄 Gereset naar standaard');
        setTimeout(() => setMsg(''), 2500);
      };
      reload();
      return p;
    });
  };

  const genPreview = async () => {
    setPreviewLoading(true);
    try {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf-layouts/preview/${activeType}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides: cur }),
      });
      if (!r.ok) throw new Error(`Preview mislukt (${r.status})`);
      const blob = await r.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setMsg('❌ Preview: ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!layouts) return <div className="p-4 text-gray-400 text-sm">Laden...</div>;

  return (
    <div className="section-card" data-testid="pdf-layout-editor">
      <div className="section-header flex items-center justify-between">
        <span className="section-title inline-flex items-center gap-2">
          <FilePdf size={18} weight="bold" /> PDF-layouts per documenttype
        </span>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs font-bold">{msg}</span>}
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={resetType} data-testid="pdf-reset">
            <ArrowCounterClockwise size={13} /> Reset
          </button>
          <button className="btn btn-secondary btn-sm inline-flex items-center gap-1" onClick={genPreview} disabled={previewLoading} data-testid="pdf-preview">
            <Eye size={13} /> {previewLoading ? 'Bezig...' : 'Preview'}
          </button>
          <button className={`btn btn-sm inline-flex items-center gap-1 ${dirty ? 'btn-primary' : 'btn-secondary'}`} onClick={save} disabled={!dirty} data-testid="pdf-save">
            <FloppyDisk size={13} /> Opslaan
          </button>
        </div>
      </div>

      <div className="section-body p-0">
        {/* Type tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 flex-wrap">
          {TYPES.map(t => {
            const active = activeType === t.id;
            const color = layouts[t.id]?.accent_color || '#2c2c2c';
            return (
              <button
                key={t.id}
                onClick={() => { setActiveType(t.id); setPreviewUrl(''); }}
                className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-r border-gray-200 transition ${active ? 'bg-white text-gray-900' : 'text-gray-500 hover:bg-white/60'}`}
                style={active ? { borderBottom: `3px solid ${color}`, marginBottom: '-1px' } : {}}
                data-testid={`pdf-type-${t.id}`}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: config form */}
          <div className="space-y-4">
            {/* Accent kleur */}
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">🎨 Accent-kleur (titel + totalen)</h4>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="color"
                  className="w-12 h-9 border border-gray-300 cursor-pointer"
                  value={cur.accent_color || '#2c2c2c'}
                  onChange={e => updateField('accent_color', e.target.value)}
                  data-testid="accent-color"
                />
                <input
                  type="text"
                  className="field-input font-mono text-xs flex-1"
                  value={cur.accent_color || ''}
                  onChange={e => updateField('accent_color', e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COLOR_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => { updateField('accent_color', p.value); updateField('table_header_bg', p.value); }}
                    className="text-[10px] px-2 py-1 border border-gray-200 hover:border-gray-500 transition"
                    style={{ background: p.value, color: '#fff' }}
                    data-testid={`preset-${p.value.slice(1)}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabelkop-kleur */}
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">📊 Tabelkop-kleur</h4>
              <div className="flex items-center gap-2">
                <input type="color" className="w-12 h-9 border border-gray-300 cursor-pointer" value={cur.table_header_bg || '#2c2c2c'} onChange={e => updateField('table_header_bg', e.target.value)} data-testid="table-bg" />
                <input type="text" className="field-input font-mono text-xs flex-1" value={cur.table_header_bg || ''} onChange={e => updateField('table_header_bg', e.target.value)} />
              </div>
            </div>

            {/* Kolommen */}
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">📑 Zichtbare tabel-kolommen</h4>
              <div className="space-y-1.5">
                {[
                  { key: 'show_ref_col', label: 'Ref.-kolom (artikelcode)' },
                  { key: 'show_prijs_col', label: 'Prijs-kolom' },
                  { key: 'show_btw_col', label: 'BTW-kolom' },
                  { key: 'show_totaal_col', label: 'Totaal-kolom' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1">
                    <input type="checkbox" checked={!!cur[key]} onChange={e => updateField(key, e.target.checked)} data-testid={`${key}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-1 italic">Tip: leverbonnen tonen meestal geen prijs.</p>
            </div>

            {/* Optionele blokken */}
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">📦 Optionele blokken</h4>
              <div className="space-y-1.5">
                {[
                  { key: 'show_vervaldag', label: 'Toon Vervaldag (in rood)' },
                  { key: 'show_geldig_tot', label: 'Toon "Geldig tot" (voor offertes)' },
                  { key: 'show_payment_box', label: 'Toon betalingsblok (IBAN + QR)' },
                  { key: 'date_highlight', label: 'Datum in accent-kleur markeren' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1">
                    <input type="checkbox" checked={!!cur[key]} onChange={e => updateField(key, e.target.checked)} data-testid={`${key}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom datum-label */}
            <div className="border border-gray-200 p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">🏷️ Datum-label</h4>
              <input
                type="text"
                className="field-input text-sm"
                value={cur.date_label || ''}
                placeholder="bv. Vervaldag / Geldig tot / Gewenste leverdatum"
                onChange={e => updateField('date_label', e.target.value)}
                data-testid="date-label"
              />
              <p className="text-[10px] text-gray-500 mt-1 italic">Verschijnt in de meta-sectie naast Datum/Leveringsdatum.</p>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="border border-gray-200 bg-gray-50 flex flex-col min-h-[600px]">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider text-gray-600 flex items-center justify-between">
              <span>👁️ Live voorbeeld</span>
              {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline normal-case">Open in nieuw venster</a>}
            </div>
            {previewUrl ? (
              <iframe src={previewUrl} title="Preview" className="flex-1 border-0 bg-white" data-testid="pdf-preview-iframe" />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-gray-400">
                <Eye size={48} weight="thin" />
                <p className="mt-3 text-sm">Klik <strong>Preview</strong> om een voorbeeld te genereren met uw huidige instellingen</p>
                <p className="text-[11px] mt-1 italic">Gebruikt demo-data (€ 399,30 totaal)</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
